// @ts-nocheck - untyped
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import WebScraperService from '../../../Services/WebScraperService';
import Dropzone from 'react-dropzone';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CancelIcon from '@mui/icons-material/Cancel';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CountryFlag } from './CountryHelpers';
import Notification from '../../Layout/Notification';
import '../../../App.scss';
import './WebScraper.scss';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SendIcon from '@mui/icons-material/Send';
import { useAuth } from '../../../Context/AuthenticationContext';
import { hasAccess } from '../../../Utils/roles';
import { WEB_SCRAPER_POSTPAYMENT_ROLE } from '../../../config';

const StatusIcon = ({ status }) => {
  switch (status) {
    case 'Done':
      return <CheckCircleIcon color="success" className="ws-status-icon" />;
    case 'Paused':
      return <PauseCircleIcon color="warning" className="ws-status-icon" />;
    case 'In progress':
      return <ScheduleIcon color="primary" className="ws-status-icon" />;
    case 'Canceled':
      return <CancelIcon color="error" className="ws-status-icon" />;
    case 'Failed':
    default:
      return <ErrorOutlineIcon color="error" className="ws-status-icon" />;
  }
};
StatusIcon.propTypes = { status: PropTypes.string };

const StatusBadge = ({ status }) => {
  const s = (status || '').toLowerCase();
  let color = 'default';
  if (s.includes('done')) color = 'success';
  else if (s.includes('progress')) color = 'info';
  else if (s.includes('paused') || s.includes('cancel')) color = 'warning';
  else if (s.includes('fail')) color = 'error';
  return <Chip size="small" label={status || '-'} color={color} />;
};
StatusBadge.propTypes = { status: PropTypes.string };

function formatUserName(value) {
  if (!value || !String(value).includes('@')) return 'Unknown user';
  const namePart = value.substring(0, value.lastIndexOf('@'));
  if (!namePart.includes('.')) return namePart;
  return namePart
    .split('.')
    .map(p => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
}

const getCountryNameFromList = (countries, code) => {
  const cc = (code || '').toUpperCase();
  const item = Array.isArray(countries)
    ? countries.find(c => (c.code || '').toUpperCase() === cc)
    : null;
  return item?.name || null;
};

const withinLastDays = (dateLike, days = 7) => {
  if (!dateLike) return false;
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const windowMs = days * 24 * 60 * 60 * 1000;
  return diffMs >= 0 && diffMs <= windowMs;
};

const WebScrapperUploadFile = ({ onJobStarted }) => {
  const { user } = useAuth();
  const canUseServiceNow = hasAccess(user?.roles, [WEB_SCRAPER_POSTPAYMENT_ROLE]);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [country, setCountry] = useState('');
  const [countries, setAvailableCountries] = useState({ data: [], isLoading: true });

  // recent jobs and mini stats
  const [recent, setRecent] = useState([]);
  const [miniStats, setMiniStats] = useState({
    inProgress: 0,
    paused: 0,
    done: 0,
    failed: 0,
    canceled: 0,
  });

  useEffect(() => {
    let alive = true;

    async function fetchAvailableCountries() {
      try {
        const response = await WebScraperService.getAvailableCountries();
        const incoming = response?.data;
        const list = Array.isArray(incoming)
          ? incoming
          : Array.isArray(incoming?.data)
            ? incoming.data
            : [];
        if (alive) {
          setAvailableCountries({ data: list, isLoading: false });
          if (list.length) setCountry(prev => prev || list[0].code);
        }
      } catch (ex) {
        Notification.showError(
          `Unexpected error occurred. Please try later (description: ${ex.message})`
        );
        if (alive) setAvailableCountries({ data: [], isLoading: false });
      }
    }

    async function fetchRecent() {
      try {
        const { data } = await WebScraperService.getScrapingResults(1, 250);
        const rows = Array.isArray(data?.result) ? data.result : [];
        const pickDate = r => r?.cratedDate || r?.createdDate || r?.createdAt;
        const last7 = rows.filter(r => withinLastDays(pickDate(r), 7));
        if (alive) setRecent(last7);
      } catch {
        // ignore errors
      }
    }

    async function fetchMiniStats() {
      try {
        const { data } = await WebScraperService.getScrapingResults(1, 250);
        const rows = Array.isArray(data?.result) ? data.result : [];
        const pickDate = r => r?.cratedDate || r?.createdDate || r?.createdAt;

        const last7 = rows.filter(r => withinLastDays(pickDate(r), 7));

        const counts = last7.reduce(
          (acc, r) => {
            const s = (r.status || '').toLowerCase();
            if (s.includes('progress')) acc.inProgress += 1;
            else if (s.includes('paused')) acc.paused += 1;
            else if (s.includes('done')) acc.done += 1;
            else if (s.includes('canceled')) acc.canceled += 1;
            else if (s.includes('fail')) acc.failed += 1;
            return acc;
          },
          { inProgress: 0, paused: 0, done: 0, failed: 0, canceled: 0 }
        );

        if (alive) setMiniStats(counts);
      } catch {
        // ignore errors
      }
    }

    fetchAvailableCountries();
    fetchRecent();
    fetchMiniStats();
    const poll = setInterval(fetchMiniStats, 7000);

    return () => {
      alive = false;
      if (poll) clearInterval(poll);
    };
  }, []);

  const handleFileUpload = acceptedFiles => {
    setFile(acceptedFiles?.[0] || null);
  };

  const handleCountrySelect = ({ target }) => {
    setCountry(target.value);
  };

  const clearFile = () => setFile(null);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!file || !country) {
      Notification.showError('Please select a country and a file.');
      return;
    }
    setLoading(true);

    const data = new FormData();
    data.append('file', file);

    const reportRequest = { country: country.trim() || 'AT', file: data };

    try {
      const resp = await WebScraperService.scrapeCountry(reportRequest);
      if (resp?.status === 202 && resp?.data?.jobId) {
        Notification.showSuccess(
          `Job started (ID: ${resp.data.jobId}). Check status in the Job Monitor tab.`
        );
        onJobStarted?.();
      } else {
        Notification.showSuccess('Request submitted. Check the Job Monitor tab.');
        onJobStarted?.();
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Unknown error occurred';
      Notification.showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatCountryLabel = code => {
    const name = getCountryNameFromList(countries.data, code);
    const cc = (code || '').toUpperCase();
    if (cc === 'EP') {
      return name ? `${name} (${cc})` : 'EP';
    }
    return name ? `${name} (${cc})` : cc;
  };

  return (
    <div id="web-scraper-upload-container">
      {loading && <div className="spinner"></div>}
      <div className={`${loading ? 'blur' : ''}`}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3} alignItems="stretch">
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <Card variant="outlined" className="ws-upload-card">
                <CardHeader
                  title="Start a scraping job"
                  subheader="Pick a country and upload your .xlsx file"
                  titleTypographyProps={{ variant: 'h4', fontWeight: 'bold' }}
                  subheaderTypographyProps={{ variant: 'body2' }}
                />
                <CardContent>
                  <FormControl fullWidth className="ws-select-mb">
                    <InputLabel id="countryChooseLbl">
                      {countries.isLoading ? 'Loading…' : 'Target country'}
                    </InputLabel>
                    <Select
                      value={country}
                      onChange={handleCountrySelect}
                      label="Target country"
                      labelId="countryChooseLbl"
                      MenuProps={{ style: { maxHeight: 400 } }}
                      renderValue={val => {
                        const selected = (countries.data || []).find(c => c.code === val);
                        if (!selected) return val;
                        return (
                          <div className="ws-flex-center">
                            <CountryFlag code={selected.code} />
                            <span>
                              {selected.name} ({selected.code})
                            </span>
                          </div>
                        );
                      }}
                    >
                      {!countries.isLoading &&
                        Array.isArray(countries.data) &&
                        countries.data.map(item => (
                          <MenuItem key={item.code} value={item.code}>
                            <ListItemIcon className="ws-country-icon-min">
                              <CountryFlag code={item.code} />
                            </ListItemIcon>
                            <ListItemText primary={`${item.name} (${item.code})`} />
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>

                  <div className="ws-dropzone-mb">
                    <Dropzone
                      maxFiles={1}
                      onDrop={handleFileUpload}
                      accept={{
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
                          '.xlsx',
                        ],
                      }}
                      multiple={false}
                    >
                      {({ getRootProps, getInputProps }) => (
                        <div {...getRootProps({ className: 'ws-dropzone' })}>
                          <input {...getInputProps()} />
                          <Typography variant="body2" className="ws-dropzone-hint">
                            Drag &amp; drop a .xlsx here, or click to browse
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Only Excel (.xlsx) files are accepted
                          </Typography>
                          <CloudUploadIcon className="ws-dropzone-upload-icon" />
                          {file && (
                            <div className="ws-filename-wrap">
                              <ListItemText className="ws-file-label" primary={file.name} />
                              <Tooltip title="Clear file">
                                <IconButton size="small" onClick={clearFile}>
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </div>
                          )}
                        </div>
                      )}
                    </Dropzone>
                  </div>

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      Runs in background; track progress in <b>Job Monitor</b>.
                    </Typography>
                    <Button
                      id="web-scraper-upload-start-button"
                      type="submit"
                      variant="contained"
                      disabled={!file || !country || loading}
                      startIcon={loading ? <CircularProgress size={16} /> : <SendIcon />}
                    >
                      Start scraping
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 7 }}>
              <Card variant="outlined" className="ws-side-card">
                <CardContent>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    fontWeight="bold"
                    sx={{ fontSize: 15 }}
                  >
                    Supported countries
                    {Array.isArray(countries.data) && countries.data.length > 0 && (
                      <span className="ws-chips-count">{countries.data.length}</span>
                    )}
                  </Typography>
                  <div className="ws-chips-wrap">
                    {Array.isArray(countries.data) && countries.data.length > 0 ? (
                      countries.data.map(c => (
                        <Chip
                          key={c.code}
                          icon={<CountryFlag code={c.code} size={11} />}
                          label={`${c.name}`}
                          size="small"
                          variant="outlined"
                        />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No countries loaded.
                      </Typography>
                    )}
                  </div>

                  <Accordion className="ws-accordion-mt" disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <InfoOutlinedIcon
                        color="info"
                        className="ws-accordion-info-icon"
                        titleAccess="Excel guide"
                      />
                      <Typography variant="subtitle1">Excel format (quick help)</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        className="ws-format-hint-row"
                      >
                        <InfoOutlinedIcon color="info" className="ws-format-hint-icon" />
                        Required columns in the first header row:
                      </Typography>
                      <ul className="ws-format-list">
                        <li>
                          <code>APPLICATION_NUMBER</code> - application number
                        </li>
                        <li>
                          <code>PROCEDURE</code> - prefix, e.g. <code>AT</code>, <code>EP</code>,{' '}
                          <code>FR</code>
                        </li>
                        <li>
                          <code>REGISTRATION_NUMBER</code> - publication/registration (where
                          applicable)
                        </li>
                      </ul>
                      <Typography variant="body2" color="text.secondary">
                        • Only <b>.xlsx</b> files are accepted. <br />
                        • Headers must match exactly (CASE SENSITIVE). <br />• Empty rows are
                        ignored.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>

                  {canUseServiceNow && (
                    <Alert
                      severity="info"
                      icon={<InfoOutlinedIcon />}
                      className="ws-servicenow-alert"
                    >
                      <Typography variant="body2">
                        <strong>ServiceNow:</strong> After job completion, you can create ServiceNow
                        incidents directly from the Job Monitor.
                      </Typography>
                    </Alert>
                  )}
                  <Divider className="ws-divider-my" />

                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    fontWeight="bold"
                    sx={{ fontSize: 15 }}
                  >
                    Activity • last 7 days
                  </Typography>
                  <Stack direction="row" spacing={1} className="ws-activity-chips">
                    <Chip
                      label={`In progress: ${miniStats.inProgress}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    <Chip
                      label={`Done: ${miniStats.done}`}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                    <Chip
                      label={`Failed: ${miniStats.failed}`}
                      size="small"
                      color="error"
                      variant="outlined"
                    />
                    <Chip
                      label={`Canceled: ${miniStats.canceled}`}
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  </Stack>

                  <Divider className="ws-divider-my" />

                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    fontWeight="bold"
                    sx={{ fontSize: 15 }}
                  >
                    Recent jobs <span className="ws-recent-suffix">· last 7 days</span>
                  </Typography>
                  {recent.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No recent activity yet.
                    </Typography>
                  ) : (
                    <Paper variant="outlined" className="ws-recent-table-wrap">
                      <Table size="small" aria-label="recent jobs">
                        <TableHead>
                          <TableRow>
                            <TableCell>Country</TableCell>
                            <TableCell>User</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>File</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {recent.map(row => {
                            const code = row.country;
                            const label = formatCountryLabel(code);
                            const title = label;
                            return (
                              <TableRow key={row._id}>
                                <TableCell>
                                  <div className="ws-flex-center">
                                    <CountryFlag code={code} title={title} />
                                    <span>{label}</span>
                                  </div>
                                </TableCell>
                                <TableCell>{formatUserName(row.user)}</TableCell>
                                <TableCell>
                                  <StatusBadge status={row.status} />
                                </TableCell>
                                <TableCell title={row.fileName || ''}>
                                  <Typography variant="body2" noWrap className="ws-recent-filename">
                                    {row.fileName || '(no name)'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Paper>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </form>
      </div>
    </div>
  );
};

WebScrapperUploadFile.propTypes = {
  onJobStarted: PropTypes.func,
};

export default WebScrapperUploadFile;
